// @file Vuex Store module for Settings panels and subpanels
import { ContentProtectionSetting } from '@@/bits/surface_settings_helper'
import { useNativeAppStore } from '@@/pinia/native_app'
import { useSurfaceShareLinksStore } from '@@/pinia/surface_share_links'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import { WallApi } from '@@/surface/api/wall'
import type { Wall as WallApiType } from '@@/types'
import type { RootState } from '@@/vuexstore/surface/types'
import type { ContentModeration, Wall } from '@padlet/arvo'
import { GroupByTypes, validateWall } from '@padlet/arvo'
import { cloneDeep, isEqual, merge } from 'es-toolkit'
import { nextTick } from 'vue'
import type { ActionContext, Module } from 'vuex'

enum SettingsSubpanel {
  Main = 'main',
  MapStyle = 'mapstyle',
  Wallpaper = 'wallpaper',
  Icon = 'icon',
  Address = 'address',
  GroupBy = 'groupby',
  Comments = 'comments',
  Reactions = 'reactions',
  PostProperties = 'postProperties',
  WhiteboardAllowedTools = 'whiteboardAllowedTools',
  WhiteboardDefaultCardBackground = 'whiteboardDefaultCardBackground',
  WhiteboardThemeFrameBackground = 'whiteboardThemeFrameBackground',
}

interface SettingsPanelState {
  activePanel: SettingsSubpanel | null
  isSaving: boolean
  previewAttributes: Partial<Wall>
  isWallNameAvailable: boolean
  isWallNameValid: boolean
  isReactionDataValid: boolean
  arePreviewAttributesValid: boolean
  lastAvailableWallName: string | null
}

type SettingsActionContext = ActionContext<SettingsPanelState, RootState>

const defaultState = (): SettingsPanelState => ({
  activePanel: null,
  isSaving: false,
  previewAttributes: {},
  isWallNameAvailable: true,
  isWallNameValid: true,
  isReactionDataValid: true,
  arePreviewAttributesValid: true,
  lastAvailableWallName: null,
})

const SettingsModule: Module<SettingsPanelState, RootState> = {
  namespaced: true,
  state: defaultState,
  getters: {
    arePreviewAttributesDirty(state, _, rootState): boolean {
      return !isEqual(state.previewAttributes, rootState.wall)
    },
    isOpen(state): boolean {
      return state.activePanel !== null
    },
    activePanel(state): SettingsSubpanel | null {
      return state.activePanel
    },
    previewAttributes(state): { [key: string]: any } {
      return state.previewAttributes
    },
    wishGroupBy(state): GroupByTypes {
      return state.previewAttributes.wish_arrangement?.group_by ?? GroupByTypes.None
    },
    wishSortBy(state) {
      return state.previewAttributes.wish_arrangement?.sort_by ?? useSurfaceWishArrangementStore().defaultWishSortBy
    },
    isSaving(state): boolean {
      return state.isSaving
    },
    isPreviewing(state): boolean {
      return Object.keys(state.previewAttributes).length > 0
    },
    contentModeration(state): ContentModeration {
      return state.previewAttributes.content_moderation ?? 'none'
    },
    contentProtection(state): ContentProtectionSetting {
      const isRemakeable = state.previewAttributes.is_remakeable ?? true
      const contentProtection = state.previewAttributes.content_protection ?? 'none'
      if (!isRemakeable) {
        return contentProtection === ContentProtectionSetting.DisableAll
          ? ContentProtectionSetting.DisableAll
          : ContentProtectionSetting.NoRemakes
      }
      return ContentProtectionSetting.None
    },
  },
  mutations: {
    RESET_SETTINGS_STATE(state): void {
      Object.assign(state, defaultState())
    },
    START_PREVIEWING_ATTRIBUTES(state, previewAttributes: Wall): void {
      state.previewAttributes = previewAttributes
    },
    SET_ACTIVE_PANEL(state, panel: SettingsSubpanel): void {
      state.activePanel = panel
    },
    SET_IS_SAVING(state, isSaving: boolean): void {
      state.isSaving = isSaving
    },
    UPDATE_PREVIEW_ATTRIBUTES(state, attributes): void {
      state.previewAttributes = merge(cloneDeep(state.previewAttributes), attributes)
      // We manually update attribution_settings because es-toolkit's merge function merges arrays by index,
      // resulting in incorrect values (e.g. merging ['posts', 'comments'] with ['comments'] results in ['comments', 'comments']).
      // We want to replace the array entirely instead.
      if (attributes.attribution_settings) {
        state.previewAttributes.attribution_settings = attributes.attribution_settings
      }
    },
    UPDATE_WALL_AVAILABILITY(state, { isAvailable }: { isAvailable: boolean }): void {
      state.isWallNameAvailable = isAvailable
    },
    UPDATE_WALL_NAME_VALIDITY(state, { isValid }: { isValid: boolean }): void {
      state.isWallNameValid = isValid
    },
    UPDATE_WALL_REACTION_DATA_VALIDITY(state, { isValid }): void {
      state.isReactionDataValid = isValid
    },
    UPDATE_PREVIEW_ATTRIBUTES_VALIDITY(state, { arePreviewAttributesValid }): void {
      state.arePreviewAttributesValid = arePreviewAttributesValid
    },
    UPDATE_LAST_CHECKED_NAME(state: SettingsPanelState, name: string): void {
      state.lastAvailableWallName = name
    },
  },
  actions: {
    startWallAttributesPreview({ commit, rootState }): void {
      commit('START_PREVIEWING_ATTRIBUTES', { ...rootState.wall })
    },
    stopWallAttributesPreview({ commit }): void {
      commit('RESET_SETTINGS_STATE')
    },
    hideSettingsPanel({ commit }): void {
      commit('RESET_SETTINGS_STATE')
    },
    showSettingsPanel({ dispatch }, activePanel: SettingsSubpanel = SettingsSubpanel.Main): void {
      dispatch('startWallAttributesPreview')
      dispatch('changeActivePanel', activePanel)
    },
    changeActivePanel({ commit }, panel: SettingsSubpanel): void {
      commit('SET_ACTIVE_PANEL', panel)
    },
    async saveSettings({ commit, dispatch, state }): Promise<boolean> {
      try {
        commit('SET_IS_SAVING', true)

        // Snapshot previewAttributes before save to detect local changes during request
        const previewSnapshot = cloneDeep(state.previewAttributes)

        const updatedWall = await WallApi.update(state.previewAttributes as Partial<WallApiType>)
        dispatch('updateWall', updatedWall, { root: true })

        // Only sync previewAttributes if user hasn't edited during the save request
        // This prevents overwriting local changes made while the request was in flight
        if (isEqual(state.previewAttributes, previewSnapshot)) {
          dispatch('updateAndValidatePreviewAttributes', updatedWall)
        }

        commit('SET_IS_SAVING', false)
        void useNativeAppStore().postSurfaceState()

        // clear manually so we don't have to check for links again after saving settings
        if (updatedWall.wish_arrangement.group_by !== GroupByTypes.Section) {
          useSurfaceShareLinksStore().setSectionBreakoutLinksState(null)
        }
        return true
      } catch {
        commit('SET_IS_SAVING', false)
        return false
      }
    },
    toggleSettingsPanel({ dispatch, getters }, panel: SettingsSubpanel): void {
      if (getters.isOpen) {
        dispatch('hideSettingsPanel')
      } else {
        // there is a weird issue where the panel is set as a pointer event due to SurfaceActionBar / SurfaceActionMoreMenu
        // so in those cases we just set the activePanel as Main.
        dispatch('showSettingsPanel', typeof panel !== 'string' ? SettingsSubpanel.Main : panel)
      }
    },
    updatePreviewAttributes({ commit, rootState }: SettingsActionContext, attributes): void {
      if (attributes?.background?.url && attributes.background.url !== rootState.wall.background.url) {
        // if the background url changes and there is no is_custom specified (usually from native bridge) => it's a custom background
        attributes.background.is_custom = attributes.background.is_custom ?? true
      }

      // Native bridge only knows about the singular "effect", not the array "effects"
      if (attributes?.background?.effect !== null && attributes?.background?.effect !== undefined) {
        attributes.background.effects = [attributes.background.effect]
      }
      commit('UPDATE_PREVIEW_ATTRIBUTES', attributes)
    },
    updateAndValidatePreviewAttributes({ getters, dispatch }: SettingsActionContext, attributes): void {
      void dispatch('updatePreviewAttributes', attributes)
      if (getters.arePreviewAttributesDirty as boolean) {
        nextTick(() => {
          void dispatch('updateWallValidity')
        })
      }
    },
    updateBackgroundMetadata({ dispatch }, { dominantColourAsRgb, imageLuminance }): void {
      const attributes = {
        background: {
          dominant_color_as_rgb: dominantColourAsRgb,
          luminance_name: imageLuminance,
          primary_color_as_rgb: dominantColourAsRgb,
        },
      }
      dispatch('updatePreviewAttributes', attributes)
    },
    async resetWallNameToKey({ dispatch, rootGetters }): Promise<void> {
      dispatch('updatePreviewAttributes', { name: rootGetters.publicKey })
      await dispatch('updateWallValidity')
    },
    async updateWallValidity({ commit, state, rootState, rootGetters }: SettingsActionContext): Promise<void> {
      try {
        // reset validity
        commit('UPDATE_WALL_NAME_VALIDITY', { isValid: true })
        commit('UPDATE_WALL_REACTION_DATA_VALIDITY', { isValid: true })
        let arePreviewAttributesValid = true
        const previewAttributes = state.previewAttributes as Wall
        await validateWall(previewAttributes)

        // check if name is different and update wall name availability
        const { name } = state.previewAttributes as Wall
        if (name && name !== rootState.wall.name && name !== state.lastAvailableWallName) {
          // Check against last queried name so we don't make wasted queries against
          // a name that has already been validated in this round of settings updates.
          const isAvailable = (await WallApi.checkAvailability(name, rootGetters.wallHashid)).available
          commit('UPDATE_WALL_AVAILABILITY', { isAvailable })
          commit('UPDATE_LAST_CHECKED_NAME', name)
          arePreviewAttributesValid = arePreviewAttributesValid && isAvailable
        }
        commit('UPDATE_PREVIEW_ATTRIBUTES_VALIDITY', { arePreviewAttributesValid })
      } catch (e) {
        const reasons = e.reasons || []
        const isWallNameInvalid = reasons.find((reason: { field: string }): boolean => reason.field === 'name')
        if (isWallNameInvalid) {
          commit('UPDATE_WALL_NAME_VALIDITY', { isValid: false })
        }
        const isReactionDataInvalid = reasons.find(
          (reason: { field: string }): boolean => reason.field === 'reaction_data',
        )
        if (isReactionDataInvalid) {
          commit('UPDATE_WALL_REACTION_DATA_VALIDITY', { isValid: false })
        }
        commit('UPDATE_PREVIEW_ATTRIBUTES_VALIDITY', { arePreviewAttributesValid: false })
      }
    },
  },
}
export default SettingsModule
export { SettingsSubpanel }
export type { SettingsPanelState }
